import React, { useMemo } from 'react';
import type { EventScheduleFragment } from '@graphql/generated';
import type { ScheduleListTranslations } from '../ScheduleList.i18n';
import type { VariantStyles } from '../ScheduleList.types';
import { getCalendarButtonProps, ScheduleListTestIds } from '../ScheduleList.utils';
import { Box, ButtonV2, Flex, LinkV2, TextV2 } from '@withjoy/joykit';
import { InfoBlock } from '../../../primitives/InfoBlock';
import { DescriptionBlock, DressCodeBlock, MapBlock } from '../../../components/ItineraryBlocks';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { useLayout } from '@shared/context/LayoutProvider';
import { useColorPaletteProvider } from '../../../layouts/ColorPaletteProvider/ColorPaletteProvider';
import { body2ToParagraphOverride, body3ToParagraphOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { getGoogleMapsSearchLink } from '../../../common/utils/getGoogleMapsSearchLink';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { pxToRem } from '@withjoy/joykit/theme';

const ARROW_RIGHT = '→';
const CTA_BUTTON_OVERRIDES = [...body3ToParagraphOverride, ...body2ToParagraphOverride];

type PrimaryBlockProps = Readonly<{
  eventId: string;
  isMobile?: boolean;
  timeframe?: string;
  eventHandle: string;
  title: Maybe<string>;
  placeId: Maybe<string>;
  showMap: Maybe<boolean>;
  location: Maybe<string>;
  dressCode: Maybe<string>;
  description: Maybe<string>;
  item?: EventScheduleFragment;
  calendarDescription?: string;
  eventNameMarkup?: JSX.Element;
  isToBeAnnounced: Maybe<boolean>;
  virtualEventMarkup?: JSX.Element;
  shouldShowWhereToStayButton: boolean;
  shouldShowAddToCalendarButton: boolean;
  translations: ScheduleListTranslations;
  styleProps: VariantStyles['primaryBlock'];
  testIds: ScheduleListTestIds['listItemTestIds'];
  onAddressClicked?: (scheduleTitle: string) => void;
  onCalendarClicked?: (title: string, type: string) => void;
  scheduleListVersion: 'v1' | 'v2';
}>;

export const PrimaryBlock: React.FC<PrimaryBlockProps> = props => {
  const {
    title,
    description,
    dressCode,
    isToBeAnnounced,
    translations,
    eventNameMarkup,
    location,
    placeId,
    showMap,
    styleProps,
    testIds,
    virtualEventMarkup,
    onAddressClicked,
    timeframe,
    calendarDescription,
    isMobile,
    item,
    onCalendarClicked,
    eventHandle,
    shouldShowWhereToStayButton,
    shouldShowAddToCalendarButton,
    scheduleListVersion,
  } = props;
  const { accentColor } = useColorPaletteProvider();
  const calendarButtonStyles = useMemo(() => getCalendarButtonProps(accentColor), [accentColor]);
  const hotelRoutePaths = useHotelRoutePaths();
  const { layout } = useLayout();

  const showLinkButtons = scheduleListVersion === 'v2' || (scheduleListVersion === 'v1' && !isMobile);

  const shouldShowAddToCalendar = shouldShowAddToCalendarButton && showLinkButtons && timeframe && item?.startTime?.timestamp;

  // Sometimes people add empty text to description, need to check for content.
  if (!eventNameMarkup && !virtualEventMarkup && !description?.trim() && !isToBeAnnounced && !dressCode && !location && !shouldShowAddToCalendar && !shouldShowWhereToStayButton) {
    return null;
  }

  return (
    <Box
      {...testIds.primaryBlock.prop}
      {...styleProps.container}
    >
      <InfoBlock.Group spread={scheduleListVersion === 'v1' ? 'md' : 'xs'}>
        {eventNameMarkup}
        {virtualEventMarkup}
        {description && <DescriptionBlock {...testIds.descriptionBlock.prop}>{description}</DescriptionBlock>}
        {isToBeAnnounced && !description && <DescriptionBlock {...testIds.descriptionBlock.prop}>{translations.toBeAnnouncedDescription}</DescriptionBlock>}
        {dressCode && <DressCodeBlock {...testIds.dressCodeblock.prop}>{dressCode}</DressCodeBlock>}
        {(location || placeId) && (
          <MapBlock
            handleClick={() => {
              if (onAddressClicked) {
                onAddressClicked(title ?? '');
              }
            }}
            {...testIds.mapBlock.prop}
            location={location}
            placeId={placeId}
            showMap={!!showMap}
          />
        )}
        <Flex
          gap={5}
          flexWrap={'wrap'}
          justifyContent={layout === 'brannan' ? 'flex-start' : 'center'}
          {...(scheduleListVersion === 'v2' ? { flexDirection: layout === 'brannan' ? ['column', null, 'row'] : 'column', alignItems: 'flex-start' } : {})}
        >
          <GuestSiteTypographyOverride override={CTA_BUTTON_OVERRIDES}>
            {shouldShowAddToCalendar && (
              <AddToCalendar
                start={item?.startTime?.timestamp}
                end={item?.endTime?.timestamp}
                title={item?.name}
                location={item?.location ?? ''}
                description={calendarDescription}
                handleClickAddToCalendar={onCalendarClicked}
              >
                <ButtonV2
                  {...testIds.addToCalendar.prop}
                  variant="ghost"
                  shape="rounded"
                  {...calendarButtonStyles}
                  _hover={{ background: 'transparent', color: calendarButtonStyles.color }}
                  paddingX={0}
                  size="sm"
                  gap={pxToRem(2)}
                  typographyVariant="body2"
                >
                  <TextV2
                    typographyVariant="body2"
                    textDecorationStyle="dotted"
                    textDecorationThickness="auto"
                    textDecorationLine="underline"
                    css={{
                      textUnderlineOffset: '25%',
                      textUnderlinePosition: 'from-font',
                      textDecorationSkipInk: 'none',
                    }}
                  >
                    {translations.addToCalendar}
                  </TextV2>
                  {ARROW_RIGHT}
                </ButtonV2>
              </AddToCalendar>
            )}
            {shouldShowWhereToStayButton && (
              <ButtonV2
                {...testIds.getDirections.prop}
                variant="ghost"
                shape="rounded"
                {...calendarButtonStyles}
                _hover={{ backgroundColor: 'transparent', color: calendarButtonStyles.color }}
                paddingX={0}
                size="sm"
                fontWeight={400}
                typographyVariant="body2"
                gap={pxToRem(2)}
              >
                <LinkV2
                  href={hotelRoutePaths.bookYourStay.buildRelativeUrl(eventHandle, {
                    utm_medium: 'web',
                    utm_source: 'schedule_list_item',
                    utm_campaign: 'schedule_item_where_to_stay',
                  })}
                  isExternal={true}
                  color={calendarButtonStyles.color}
                  _hover={{ color: calendarButtonStyles.color }}
                  _visited={{ color: calendarButtonStyles.color }}
                  textDecorationStyle="dotted"
                  textDecorationThickness="auto"
                  textDecorationLine="underline"
                  css={{
                    textUnderlineOffset: '25%',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                  }}
                >
                  {translations.whereToStay}
                </LinkV2>
                {ARROW_RIGHT}
              </ButtonV2>
            )}
            {item?.location && showLinkButtons ? (
              <ButtonV2
                {...testIds.getDirections.prop}
                variant="ghost"
                shape="rounded"
                gap={pxToRem(2)}
                {...calendarButtonStyles}
                _hover={{ backgroundColor: 'transparent', color: calendarButtonStyles.color }}
                paddingX={0}
                size="sm"
                typographyVariant="body2"
              >
                <LinkV2
                  href={getGoogleMapsSearchLink(item?.location)}
                  isExternal={true}
                  color={calendarButtonStyles.color}
                  _hover={{ color: calendarButtonStyles.color }}
                  _visited={{ color: calendarButtonStyles.color }}
                  textDecorationStyle="dotted"
                  textDecorationThickness="auto"
                  textDecorationLine="underline"
                  css={{
                    textUnderlineOffset: '25%',
                    textUnderlinePosition: 'from-font',
                    textDecorationSkipInk: 'none',
                  }}
                >
                  {translations.getDirections}
                </LinkV2>
                {ARROW_RIGHT}
              </ButtonV2>
            ) : null}
          </GuestSiteTypographyOverride>
        </Flex>
      </InfoBlock.Group>
    </Box>
  );
};
